import { err, ok, Result } from "neverthrow";
import { IValidationService } from "@/framework/validation/IValidationService";
import { GraphqlApi, IGraphqlApi } from "@/framework/network/gql/GraphqlApi";
import { ValidationService } from "@/framework/validation/ValidationService";
import { ZodError } from "zod";
import { QcUser, QcUserSchema } from "@/modules/staff/objects/conversation/QcUser";
import {
  BeforeLogoutDocument,
  CreateSsoLinkDocument,
  GetQcUserDocument,
  GetSrcUserTypeDocument,
  ReceiveMyEventDocument,
  RemovePushTokenDocument,
  SsoLink,
  UpdatePushTokenDocument,
} from "@/framework/network/gql/generated/graphql";
import GraphqlApiError from "@/framework/network/gql/GraphqlApiError";
import { isEmpty } from "@/framework/utilities/isEmpty";
import {
  BeforeLogoutMutation,
  CreateSsoLinkMutation,
  GetQcUserQuery,
  GetSrcUserTypeQuery,
  ReceiveMyEventMutation,
  RemovePushTokenMutation,
  UpdatePushTokenMutation,
} from "../network/gql/generated/graphql.stg";
import { GeneralResponse, GeneralResponseSchema } from "../objects/GeneralResponse";
import { SsoLinkSchema } from "@/modules/staff/objects/loginStatus/SsoLink";
import { SrcUserTypeResponse, SrcUserTypeResponseSchema } from "@/modules/auth/objects/SrcUserTypeResponse";
import { GeneralResult, GeneralResultSchema } from "../objects/GeneralResult";

export class AuthService {
  private _graphql: IGraphqlApi;
  private _validation: IValidationService;

  public constructor(
    graphql: IGraphqlApi = new GraphqlApi(),
    validation: IValidationService = new ValidationService()
  ) {
    this._graphql = graphql;
    this._validation = validation;
  }

  async beforeUserLogout(onetimeToken: string): Promise<Result<GeneralResponse, GraphqlApiError | ZodError>> {
    const res = await this._graphql.runMutationAndValidate<BeforeLogoutMutation, GeneralResponse>(
      // @ts-ignore conflict between generic typing and exact requirement
      BeforeLogoutDocument,
      GeneralResponseSchema,
      { onetimeToken }
    );

    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async fetchQcUser(noCache: boolean = false): Promise<Result<QcUser, GraphqlApiError | ZodError>> {
    let res = await this._graphql.runQueryAndValidate<GetQcUserQuery, QcUser>(
      // @ts-ignore conflict between generic typing and exact requirement
      GetQcUserDocument,
      QcUserSchema,
      {},
      noCache
    );
    if (res.isOk()) {
      return ok(res.value);
    } else {
      return err(res.error);
    }
  }

  async generateSsoLink(): Promise<Result<SsoLink, GraphqlApiError | ZodError>> {
    const res = await this._graphql.runMutationAndValidate<CreateSsoLinkMutation, SsoLink>(
      // @ts-ignore conflict between generic typing and exact requirement
      CreateSsoLinkDocument,
      SsoLinkSchema,
      {}
    );

    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async getSrcType(): Promise<Result<SrcUserTypeResponse, GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<GetSrcUserTypeQuery, SrcUserTypeResponse>(
      // @ts-ignore conflict between generic typing and exact requirement
      GetSrcUserTypeDocument,
      SrcUserTypeResponseSchema,
      {}
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async removeDeviceToken(deviceId: string): Promise<Result<GeneralResult | null, GraphqlApiError | ZodError>> {
    if (isEmpty(deviceId)) return ok(null);
    const res = await this._graphql.runMutationAndValidate<RemovePushTokenMutation, GeneralResult | null>(
      // @ts-ignore conflict between generic typing and exact requirement
      RemovePushTokenDocument,
      GeneralResultSchema.nullable(),
      { deviceId: deviceId }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async updateDeviceToken(
    fcmToken: string,
    deviceId: string = ""
  ): Promise<Result<GeneralResult | null, GraphqlApiError | ZodError>> {
    const v = {
      deviceId: deviceId,
      deviceToken: fcmToken,
      platformType: "web",
      service: "in-room",
    };
    if (isEmpty(deviceId) || isEmpty(fcmToken)) return ok(null);

    const res = await this._graphql.runMutationAndValidate<UpdatePushTokenMutation, GeneralResult | null>(
      // @ts-ignore conflict between generic typing and exact requirement
      UpdatePushTokenDocument,
      GeneralResultSchema,
      v
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async receiveEvent(v: any): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<ReceiveMyEventMutation, GeneralResponse>(
      ReceiveMyEventDocument,
      GeneralResponseSchema,
      v
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }
}
